<template>
  <v-container class="fill-height d-flex align-center justify-center">
    <v-row justify="center">
      <v-col cols="12" sm="8" md="6" lg="4">
        <v-card class="elevation-12">
          <v-card-title class="text-center text-h5 pt-4 pb-2">
            {{ $t('auth.adminPanel') }}
          </v-card-title>
          <v-card-subtitle class="text-center pb-4">
            Community Locker by Lockerbot
          </v-card-subtitle>
          
          <v-card-text>
            <v-form @submit.prevent="handleSubmit">
              <v-text-field
                class="phone-input-wrapper"
                persistent-placeholder
                placeholder="(xx) xxx-xxxx"
                v-model="phoneNumber"
                :rules="[
                  v => !!v || t('validation.required'),
                  v => /^\(\d{2}\)\s\d{3}-\d{4}$/.test(v) || t('validation.phone')
                ]"
                @input="formatPhoneNumber"
              >
                <template v-slot:prepend>
                  <div class="country-code">+84</div>
                </template>
              </v-text-field>

              <v-text-field
                class="mb-6"
                v-model="credentials.password"
                :label="$t('auth.password')"
                prepend-icon="mdi-lock"
                :type="showPassword ? 'text' : 'password'"
                variant="outlined"
                required
                :append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
                @click:append="showPassword = !showPassword"
                :rules="[
                  v => !!v || t('validation.required')
                ]"
              ></v-text-field>

              <v-alert
                v-if="error"
                type="error"
                class="mb-6"
                density="compact"
              >
                {{ error }}
              </v-alert>

              <v-btn
                color="primary"
                type="submit"
                block
                :loading="loading"
                class="mt-4 login-btn"
                :elevation="2"
              >
                {{ $t('auth.signIn') }}
              </v-btn>
            </v-form>
          </v-card-text>

          <v-divider class="mt-4"></v-divider>
          
          <v-card-text class="text-caption text-center py-4 support-text">
            {{ $t('auth.forgotPassword') }}
            {{ $t('auth.contactSupport') }}
            <a 
              href="mailto:support@lockerbot.io"
              class="text-decoration-none font-weight-medium"
            >
              support@lockerbot.io
            </a>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script setup>
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '@/stores/auth'

const authStore = useAuthStore()
const { t } = useI18n()
const loading = ref(false)
const error = ref(null)
const showPassword = ref(false)
const phoneNumber = ref('')

const credentials = ref({
  phone_number: '',
  password: ''
})

const formatPhoneNumber = (e) => {
  let value = e.target.value.replace(/\D/g, '')
  const match = value.match(/^(\d{0,2})(\d{0,3})(\d{0,4})/)
  if (!match) return

  let formatted = ''
  if (match[1]) {
    formatted += `(${match[1]}`
    if (match[2]) {
      formatted += `) ${match[2]}`
      if (match[3]) {
        formatted += `-${match[3]}`
      }
    }
  }

  phoneNumber.value = formatted
  credentials.value.phone_number = `+84${value}`
}

async function handleSubmit() {
  loading.value = true
  error.value = null

  try {
    await authStore.login(credentials.value)
  } catch (err) {
    error.value = err.response?.data?.detail || t('auth.loginError')
  } finally {
    loading.value = false
  }
}
</script>

<style scoped>
.v-field input {
  font-family: monospace;
  letter-spacing: 0.5px;
}

.v-container {
  min-height: 100vh;
  padding: 0;
}

.support-text {
  color: rgba(var(--v-theme-on-surface), 0.7);
  line-height: 1.5;
}

.support-text a {
  color: var(--v-theme-primary);
}

.login-btn {
  text-transform: none;
  font-weight: 500;
  letter-spacing: 0.5px;
  height: 44px;
}

.country-code {
  font-family: monospace;
  font-size: 1rem;
  font-weight: 500;
  color: rgba(var(--v-theme-on-surface), 0.9);
  padding: 0 8px;
  border-right: 1px solid rgba(var(--v-theme-on-surface), 0.12);
}

:deep(.phone-input-wrapper .v-field__input) {
  padding-left: 8px !important;
}
</style> 